import { Trans, useLingui } from '@lingui/react/macro'
import { Badge } from 'designSystem/components/Badge'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import { Text } from 'designSystem/components/Text'
import { RevisionAuditBadgeMode } from 'features/knowledgeDocumentViewer/hooks/useRevisionHistory'
import {
  type RevisionAudit,
  type RevisionAuditRow,
  RevisionRowStatus,
} from 'features/knowledgeDocumentViewer/utils/revisionAuditModel'
import { CircleAlert, CircleHelp } from 'lucide-react'

type OpenDocument = (documentId: string, pageNum: number | undefined) => void

type RevisionAuditBadgeProps = {
  size: 'xs' | 'lg'
  audit: RevisionAudit
  onOpenDocument?: OpenDocument
}

function RevisionRowStatusPill({
  status,
}: {
  status: RevisionRowStatus | null
}) {
  switch (status) {
    case RevisionRowStatus.Missing:
      return (
        <Badge variant='destructive'>
          <Trans>Missing</Trans>
        </Badge>
      )
    case RevisionRowStatus.Available:
      return (
        <Badge variant='outline'>
          <Trans>Available</Trans>
        </Badge>
      )
    case RevisionRowStatus.MayBeMissing:
      return (
        <Badge variant='outlineMuted'>
          <Trans>May be missing</Trans>
        </Badge>
      )
    case RevisionRowStatus.Matched:
      return (
        <Badge variant='outline'>
          <Trans>Matched in system</Trans>
        </Badge>
      )
    case null:
      return null
  }
}

function RevisionAuditRowView({
  row,
  onOpenDocument,
}: {
  row: RevisionAuditRow
  onOpenDocument?: OpenDocument
}) {
  const { t } = useLingui()
  const { openTarget } = row
  const revisionLabel = row.label || openTarget?.documentId || ''
  return (
    <Box display='flex' gap='sm' align='center'>
      <Box display='flex' direction='column' flex='one' minWidth='none'>
        <Text weight='bold' truncate>
          {row.label}
        </Text>
        {row.date ? (
          <Text color='muted' truncate>
            {row.date}
          </Text>
        ) : null}
        {row.description ? (
          <Text color='muted' truncate>
            {row.description}
          </Text>
        ) : null}
      </Box>
      <RevisionRowStatusPill status={row.status} />
      {openTarget && onOpenDocument ? (
        <Button
          type='button'
          variant='link'
          size='sm'
          aria-label={t`Open document for revision ${revisionLabel}`}
          onClick={() =>
            onOpenDocument(openTarget.documentId, openTarget.pageNum)
          }>
          <Trans>Open document</Trans>
        </Button>
      ) : null}
    </Box>
  )
}

/**
 * Neutral section for declared revisions the backend could not verify: "May be
 * missing" rows first, then rows matched against a revision we hold. Renders
 * nothing when there is nothing to reconcile. Never red.
 */
function NeedsReviewSection({
  needsReview,
  onOpenDocument,
}: {
  needsReview: RevisionAudit['needsReview']
  onOpenDocument?: OpenDocument
}) {
  const { matchedCount, mayBeMissingCount: unmatchedCount, rows } = needsReview
  if (matchedCount + unmatchedCount === 0) return null
  return (
    <Box display='flex' direction='column' gap='sm'>
      <Text color='muted'>
        {matchedCount > 0 && unmatchedCount > 0 ? (
          <Trans>
            {matchedCount} matched in system · {unmatchedCount} may be missing
          </Trans>
        ) : matchedCount > 0 ? (
          <Trans>{matchedCount} matched in system</Trans>
        ) : (
          <Trans>{unmatchedCount} may be missing</Trans>
        )}
      </Text>
      {rows.map((row) => (
        <RevisionAuditRowView
          key={row.key}
          row={row}
          onOpenDocument={onOpenDocument}
        />
      ))}
    </Box>
  )
}

/**
 * Warning badge for the revision-history audit, opening a popover.
 * - `missing`: lists declared revisions with missing/available status, counts,
 *   and a jump-link to the satisfying document for available ones.
 * - `undetermined`: neutral — the audit couldn't complete (unreadable sheet).
 * - `needs_review`: neutral — declared revisions the backend couldn't verify,
 *   each either matched in the system (with an Open link) or "May be missing";
 *   never red.
 * The neutral needs-review rows also render inside `missing` mode when present.
 */
export function RevisionAuditBadge({
  size,
  audit,
  onOpenDocument,
}: RevisionAuditBadgeProps) {
  const { t } = useLingui()
  const { mode } = audit
  const isUndetermined = mode === RevisionAuditBadgeMode.Undetermined
  const isNeedsReview = mode === RevisionAuditBadgeMode.NeedsReview
  const isNeutral = isUndetermined || isNeedsReview
  const { history, supportedRows, needsReview } = audit
  const declaredCount = history?.declared_count
  const availableCount = history?.available_count
  const missingCount = history?.missing_count
  const hasCounts = [declaredCount, availableCount, missingCount].some(
    (n) => typeof n === 'number'
  )
  const triggerLabel = isNeedsReview
    ? t`A declared revision may be missing`
    : isUndetermined
      ? t`The revision audit couldn’t complete and needs review`
      : t`A declared revision is missing from the uploaded documents`
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type='button'
          variant='warningRaised'
          size={size}
          aria-label={triggerLabel}>
          {isNeutral ? <CircleHelp /> : <CircleAlert />}
          {isNeedsReview ? (
            <Trans>Review revision history</Trans>
          ) : isUndetermined ? (
            <Trans>Revision audit needs review</Trans>
          ) : (
            <Trans>Missing revision</Trans>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent layout='default' fitViewport align='end'>
        <PopoverTitle>
          <Trans>Revision history</Trans>
        </PopoverTitle>
        {isUndetermined ? (
          <Text color='muted'>
            <Trans>
              The revision audit couldn’t complete — one or more sheets were
              unreadable. Needs review.
            </Trans>
          </Text>
        ) : isNeedsReview ? (
          <NeedsReviewSection
            needsReview={needsReview}
            onOpenDocument={onOpenDocument}
          />
        ) : (
          <>
            {hasCounts ? (
              <Box display='flex' gap='sm'>
                {typeof declaredCount === 'number' ? (
                  <Badge variant='outlineMuted'>
                    {declaredCount} <Trans>declared</Trans>
                  </Badge>
                ) : null}
                {typeof availableCount === 'number' ? (
                  <Badge variant='outlineMuted'>
                    {availableCount} <Trans>available</Trans>
                  </Badge>
                ) : null}
                {typeof missingCount === 'number' ? (
                  <Badge variant='destructive'>
                    {missingCount} <Trans>missing</Trans>
                  </Badge>
                ) : null}
              </Box>
            ) : null}
            {history?.findings_unavailable ? (
              <Text color='muted'>
                <Trans>Revision history couldn’t be computed.</Trans>
              </Text>
            ) : supportedRows.length > 0 ? (
              <Box display='flex' direction='column' gap='sm'>
                {supportedRows.map((row) => (
                  <RevisionAuditRowView
                    key={row.key}
                    row={row}
                    onOpenDocument={onOpenDocument}
                  />
                ))}
              </Box>
            ) : (
              <Text color='muted'>
                <Trans>
                  A declared revision is missing from the uploaded documents.
                </Trans>
              </Text>
            )}
            <NeedsReviewSection
              needsReview={needsReview}
              onOpenDocument={onOpenDocument}
            />
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
